// assignCoverToRoom — the ONE place a break cover is named on a room doc.
//
// Naming a cover is three decisions that must never come apart: the state
// machine transition (`assignCover`, which refuses a double assignment and a
// finished meal), the hand-over stamp, and the fact that `onBreak` stays FALSE
// because nobody has walked anywhere yet.
//
// ⚠️ WHY THIS FILE EXISTS. `useRoomBreakActions.assignBreak` made those three
// calls inline, and it is a hook — so the Timeline BREAKS card, which is not
// inside a room, could not reuse it and would have had to hand-write the same
// three lines. The last time a break rule was written twice, `BreakEnginePanel`
// and `VoiceControl` drifted from the card and "synced everywhere" stopped
// being true. Copied logic survives review; copied VOCABULARY does not.
//
// Same reasoning as `features/relief/confirmRelief`.

import { assignCover, stampBreakTime, nowHm } from "../../domain/breaks";
import { setRoomBreak, createTask } from "../../firebase/firestore";
import { buildBreakCoverTasks } from "../../domain/breakDirective";

/**
 * Name somebody to cover one break channel on a room.
 *
 * @param {object} room    the room doc (needs .id, .breaks, .breakTimes)
 * @param {object} opts
 * @param {string} opts.channel  breakfast | lunch | dinner | bathroom
 * @param {object} opts.giver    { id, name } — the person being asked
 * @param {object} [opts.notify] who to TELL. Omit and the write is silent —
 *        which is what this function did for its whole life, and why the AIC
 *        still had to telephone the CRNA they had just named on the board.
 *        Every `*Uid` must already be an AUTH uid (resolve with
 *        `domain/channels.uidOf`); a staff id writes cleanly and rings nobody.
 * @returns {Promise<object>} the transitioned breaks map that was written
 * @throws  whatever `assignCover` throws on an illegal transition, so the
 *          caller can surface the machine's own message rather than inventing
 *          one. The write never happens in that case.
 */
export async function assignCoverToRoom(room, { channel, giver, notify = null } = {}) {
  if (!room?.id) throw new Error("That row has no room to assign a break on.");
  if (!channel) throw new Error("No break channel was chosen.");
  if (!giver?.id) throw new Error("No cover was chosen.");

  // Throws BEFORE any write on an illegal transition — the machine owns the
  // rules, and a room must never be written into a state it refused.
  const breaks = assignCover(room.breaks || {}, channel);
  const breakTimes = stampBreakTime(room.breakTimes, channel, nowHm());

  await setRoomBreak(room.id, {
    breaks,
    // Dotted path: the sheet engine writes `breaks.<ch>` too, and a whole-map
    // write here would revert whatever it had advanced in another channel.
    channel,
    breakTimes,
    relieverId: giver.id,
    relieverName: giver.name || "",
    // ⚠️ FALSE. The room is not on a break until the named person is standing
    // in it and has taken handoff — that is their own arrival tap to make.
    onBreak: false,
  });

  // ⚠️ BEST-EFFORT, AND ALWAYS AFTER THE RECORD. The room doc is the clinical
  // fact; the three phones ringing are a convenience built on top of it. If
  // this throws — nobody resolved to a uid, a rules refusal, a dead network —
  // the cover is still named and the caller still succeeds. An AIC must never
  // be told the assignment failed because a push did. Same shape as the phase
  // directive in hooks/useCasePhases.
  //
  // ⚠️ AND IT LIVES HERE, not in the two callers. This file exists because the
  // last time a break rule was written twice, BreakEnginePanel and VoiceControl
  // drifted from the card and "synced everywhere" stopped being true. "Naming a
  // cover also tells people" is part of what naming a cover MEANS; a caller
  // that forgot would silently go back to the telephone.
  if (notify) {
    try {
      const tasks = buildBreakCoverTasks({
        ...notify,
        roomName: notify.roomName || room.name || "",
        giverName: notify.giverName || giver.name || "",
      });
      for (const task of tasks) await createTask(task);
    } catch (err) {
      console.error("Break cover directive failed:", err);
    }
  }

  return breaks;
}

export default assignCoverToRoom;
